import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { t } from '@lingui/core/macro';
import { FacilityTransferDirection } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { DatePickerQueryParam, NoddiTabs, ScreenBaseWrapper, useDatePickerQueryParam } from 'noddi-ui-web';
import { CustomDateFormats, noddiFormatDate } from 'noddi-util';
import { useParams } from 'react-router-dom';
import { getTabsTranslations } from '../../utils/commonTranslations';
import { IncomingTab } from './IncomingTab';
import { OutgoingTab } from './OutgoingTab';
import { FacilityTimeZoneContext } from './facilityTimeZone';

export const FacilityTransferPage = () => {
  const { publicSlug } = useParams() as { publicSlug: string };
  const { date, apiDate } = useDatePickerQueryParam();

  const {
    data: day,
    isPending,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getFacilityTransferDayPublic,
    input: { publicSlug, date: apiDate },
    queryConfig: { placeholderData: keepPreviousData }
  });

  if (isPending) {
    return <NoddiLoadingScreenWithTrans />;
  }
  if (error) {
    return <ApiErrorPageWithTrans error={error} />;
  }

  const pickupDay = noddiFormatDate(date, CustomDateFormats.FULL_WEEKDAY_DAY_DOT_MONTH);
  // Pickups need action, so they win when the facility both sends and receives that day.
  const defaultDirection =
    day.outgoing.items.length === 0 && day.incoming.items.length > 0
      ? FacilityTransferDirection.INCOMING
      : FacilityTransferDirection.OUTGOING;

  return (
    <FacilityTimeZoneContext.Provider value={day.timeZone.value}>
      <ScreenBaseWrapper width='md'>
        <div className='flex flex-col gap-4'>
          <header className='flex flex-col gap-3'>
            <h1 className='text-2xl font-bold text-primary sm:text-3xl md:text-4xl'>{day.facilityName}</h1>
            <DatePickerQueryParam showQuickDateShortcuts={false} />
          </header>

          <NoddiTabs<FacilityTransferDirection>
            key={apiDate}
            ariaLabel={t`Direction`}
            translations={getTabsTranslations()}
            searchParamName='tab'
            keepExistingSearchParams
            defaultValue={defaultDirection}
            tabItems={[
              {
                value: FacilityTransferDirection.OUTGOING,
                label: t`Picked up from us`,
                count: day.outgoing.items.length,
                node: <OutgoingTab outgoing={day.outgoing} facilityName={day.facilityName} pickupDay={pickupDay} />
              },
              {
                value: FacilityTransferDirection.INCOMING,
                label: t`Delivered to us`,
                count: day.incoming.items.length,
                node: <IncomingTab incoming={day.incoming} />
              }
            ]}
          />
        </div>
      </ScreenBaseWrapper>
    </FacilityTimeZoneContext.Provider>
  );
};
